/**
 * DXF attach — upload + ingest-dispatch + overlay stamp, extracted from
 * PlansTicket so the unified import wizard can attach a DXF staged at import
 * time (owner's one-process workflow: PDF + labels + DXF in one pass).
 *
 * R3-D: a .pdf is accepted too — it is vectorized server-side (page 0 for a
 * standalone upload; the plan's own PDF goes through
 * requestVectorIngest with its bake.pageIndex instead, no upload needed).
 *
 * Throws on failure; callers own the notifications.
 */

import { useProjectStore } from '../stores/projectStore';
import { uploadLargeAsset } from '../services/assetApi';
import { requestVectorIngest } from '../services/tileApi';

export async function attachDxfToOverlay(
  overlayId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  const siteId = useProjectStore.getState().meta.siteId;
  if (!siteId) throw new Error('No site loaded');
  const isPdf = /\.pdf$/i.test(file.name);
  if (!isPdf && !/\.dxf$/i.test(file.name)) throw new Error('Pick a .dxf or .pdf file');

  // Converted CAD sheets are BIG (Kfar: 142MB of ASCII DXF — over the
  // Worker's 100MB body cap, which a single-shot POST hits as a bare
  // "Failed to fetch"). uploadLargeAsset chunks >50MB via multipart.
  const uploaded = await uploadLargeAsset(
    file, siteId, 'vector', file.name,
    (loaded, total) => onProgress?.(Math.round((loaded / total) * 100)),
  );
  const result = await requestVectorIngest(siteId, uploaded.r2Key, overlayId, isPdf ? 0 : undefined);
  const live = useProjectStore.getState().data.overlays.find((o) => o.id === overlayId);
  if (!live) throw new Error('Plan no longer exists');
  const dxf = {
    srcKey: uploaded.r2Key,
    ...(isPdf ? { pageIndex: 0 } : {}),
    jobId: result.jobId,
    outPrefix: result.outPrefix,
    status: 'ingesting' as const,
    startedAt: new Date().toISOString(),
  };
  // Durable stamp: in the wizard path this runs right after addOverlay, so
  // the create can still be in flight and an updateOverlay without _version
  // SKIPS the server write silently — a refresh inside that window would
  // orphan the ingest job. Write, flush (which resolves the create), verify,
  // re-apply — the same dance as the bake-start stamp.
  for (let attempt = 0; attempt < 2; attempt++) {
    useProjectStore.getState().updateOverlay(overlayId, { dxf });
    try {
      await useProjectStore.getState().flushPendingEntityWrites();
    } catch (err) {
      console.warn('[DxfAttach] flush failed:', err);
    }
    const after = useProjectStore.getState().data.overlays.find((o) => o.id === overlayId);
    if (after?.dxf?.jobId === result.jobId && after._version != null) return;
    console.warn(`[DxfAttach] dxf stamp did not persist (attempt ${attempt + 1}); re-applying`);
  }
  console.warn('[DxfAttach] dxf stamp may not be persisted — a refresh could orphan the ingest');
}
